import { inject, Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor, HttpParams,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import {
  catchError,
  finalize,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  throwError
} from "rxjs";
import {UserService, WmHttpService, ProjectService, EmbedDbService} from "@wms/app/services";
import {SpinnerService} from "@wms/app/services/spinner/spinner.service";
import { NotificationService } from '../../notification/notification.service';
import { environment } from "@wms/app";
import {SessionTimeoutService} from "@wms/app/services/session-timeout/session-timeout.service";
import { RequestCacheService } from "./request-cache.service";

const cache = new Map<string, any>();

@Injectable({
  providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor{
  userService: UserService;
  spinnerService: SpinnerService;
  notificationService: NotificationService;
  httpService: WmHttpService;
  projectService: ProjectService;
  sessionTimeoutService: SessionTimeoutService;
  pendingRequests: Map<string, any> = new Map<string, any>();
  requestCacheService: RequestCacheService;

  constructor(private injector: Injector, private embedDBService: EmbedDbService) {
    this.userService = this.injector.get(UserService);
    this.spinnerService = this.injector.get(SpinnerService);
    this.notificationService = this.injector.get(NotificationService);
    this.httpService = this.injector.get(WmHttpService);
    this.projectService = this.injector.get(ProjectService);
    this.sessionTimeoutService = this.injector.get(SessionTimeoutService);
    this.requestCacheService = inject(RequestCacheService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let {url, method} = req;
    let currentReqOptions = this.httpService.currentReqOptions._wmMeta;
    let toasterInfo = currentReqOptions.toasterInfo;
    const methodType = method.toLocaleLowerCase();
    let response;
    let requestBody = req.body;
    let cacheKey: string;

    // Check for variables invoke call
    req = this.isVariableInvokeCall(req, url, currentReqOptions, method);
    let pendingRequest = this.requestCacheService.get(req, url);
    if (pendingRequest) {
      return pendingRequest.observable;
    }
    if (this.showSpinner(currentReqOptions)) {
      this.spinnerService.counter++;
      this.spinnerService.show(currentReqOptions.spinnerInfo?.spinnerMsg);
    }

    if (currentReqOptions._source === 'variable') {
     cacheKey = this.generateUniqueCacheKey(req.headers, req.params, req.method, url)
      const cacheEntry = cache.get(cacheKey);
      if (cacheEntry) {
        // TODO: need to clear the cache values after sometime.
        return of(new HttpResponse({
          status: 200,
          body: cacheEntry
        }));
      }
    }

    // Create a new subject for the request
    const subject = new Subject<HttpEvent<any>>();
    const observable = subject.asObservable();

    const subscription = next.handle(req)
      .pipe(
        tap((res: any )=> {
          if (currentReqOptions._source === 'variable') {
            if (res.body?.responseBody) {
              res.body['content'] = JSON.parse(res.body.responseBody).content;
              cache.set(cacheKey, res.body);
            }
          }
          if (res instanceof HttpResponse) {
            subject.next(res);
            subject.complete();
          }
        }),
        catchError(error => {
          if (toasterInfo?.error) {
            if (!toasterInfo.error.title) {
              toasterInfo.error.title = 'error';
            }
            if (!toasterInfo.error.description) {
              toasterInfo.error.description = error.message;
            }
            this.notificationService.show('error', toasterInfo.error.title, toasterInfo.error.description);
          }
          toasterInfo = null;

          return this.handleErrorResponse(error, req, next, subject);
        }),
        finalize(()=>{
          this.requestCacheService.delete(req, url);
          if(toasterInfo?.success){
            this.notificationService.show('success', toasterInfo.success.title, toasterInfo.success.description);
          }
          if (this.showSpinner(currentReqOptions)) {
            if (this.spinnerService.counter > 0) {
              this.spinnerService.counter -= 1;
            }
          }
          if (this.spinnerService.counter === 0) {
            this.spinnerService.hide();
          }
        })
      ).subscribe();
    this.requestCacheService.set(req, { observable, subject, subscription, url });
    return observable;
  }

  showSpinner(currentReqOptions: any) {
    return currentReqOptions.spinnerInfo?.showSpinner !== false && currentReqOptions._source !== 'variable'
  }

  isAggregationCall(url: string) {
    return url.includes('/aggregations?')
  }


  isVariableInvokeCall(req: any, url: any, currentReqOptions: any, method: string) {
    if (currentReqOptions._source === 'variable' && method !== 'GET') {
      let contentType = this.isAggregationCall(url) ? 'application/json' : 'application/x-www-form-urlencoded';
      req.url = environment.SERVICES_BASE_PATH + 'services/projects/' + this.projectService.getProjectId() + '/restservices/invoke';
      req.urlWithParams = environment.SERVICES_BASE_PATH + 'services/projects/' + this.projectService.getProjectId() + '/restservices/invoke';
      const deployedUrl = this.projectService.getAppDeployedUrl();
      const endpointAddress = 'https:' + (url.includes(deployedUrl) ? url : (deployedUrl + url.replace('.', '')));
      req.body = {
        endpointAddress: encodeURI(endpointAddress),
        headers: {
          'skipSecurity': "true",
          'Content-Type': contentType
        },
        method: "POST",
        requestBody: JSON.stringify(req.body)
      };
      req.headers = new HttpHeaders({ 'Content-Type': 'application/json' })  ;
    }
    return req;
  }

  handleErrorResponse(error: HttpErrorResponse, request?: HttpRequest<any>, next?: HttpHandler, subject?: Subject<any>): Observable<any> {
    if (error.status === 401) {
      return this.handle401().pipe(
        switchMap(() => {
          // retry the failed http call after login
          return (next as HttpHandler).handle(request as HttpRequest<any>);
        }),
        tap(res => {
          if (res instanceof HttpResponse) {
            subject?.next(res);
            subject?.complete();
          }
        }),
        catchError(e => {
          if (e.status !== 401) {
            return this.handleErrorResponse(e, request, next, subject);
          } else {
            subject?.error(e);
            // should ideally logout and go to login page
            return throwError(e);
          }
        })
      )
    }
    subject?.error(error);
    return throwError(error);
  }

  handle401(): Observable<any> {
    // hiding the spinner, so that user can login through sessino timout dialog
    this.spinnerService.counter = 0;
    this.spinnerService.hide();
    return new Observable<any>(observer => {
      this.sessionTimeoutService.handleSessionTimeout().subscribe(()=>{
        observer.next();
        observer.complete();
      });
    });
  }

  generateUniqueCacheKey(headers: HttpHeaders, params: HttpParams, method: string, url: string) {
    let headersStr = JSON.stringify(headers);
    let paramsStr = JSON.stringify(params);

    return `${url}:${method}:${headersStr}:${paramsStr}`;
  }
}
